/*
 * Copyright 2021 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __assign, __rest } from "tslib";
import classNames from "classnames";
import * as React from "react";
import { Classes as CoreClasses, Utils as CoreUtils, mergeRefs } from "@blueprintjs/core";
import * as Classes from "./classes";
import { Popover2 } from "./popover2";
export var ContextMenu2 = function (_a) {
    var _b;
    var content = _a.content, children = _a.children, _c = _a.transitionDuration, transitionDuration = _c === void 0 ? 100 : _c, restProps = __rest(_a, ["content", "children", "transitionDuration"]);
    var _d = React.useState({ left: 0, top: 0 }), targetOffset = _d[0], setTargetOffset = _d[1];
    var _e = React.useState(false), isOpen = _e[0], setIsOpen = _e[1];
    var handleContextMenu = React.useCallback(function (e) {
        // support nested menus (inner menu target would have called preventDefault())
        if (e.defaultPrevented) {
            return;
        }
        e.preventDefault();
        var newTargetOffset = { left: e.clientX, top: e.clientY };
        setTargetOffset(newTargetOffset);
        setIsOpen(true);
    }, []);
    var cancelContextMenu = React.useCallback(function (e) { return e.preventDefault(); }, []);
    var handlePopoverInteraction = React.useCallback(function (nextOpenState) {
        if (!nextOpenState) {
            setIsOpen(false);
        }
    }, []);
    var targetRef = React.useRef();
    var renderTarget = React.useCallback(function (_a) {
        var ref = _a.ref;
        return (React.createElement("div", { className: Classes.CONTEXT_MENU2_POPOVER_TARGET, style: targetOffset, ref: mergeRefs(ref, targetRef) }));
    }, [targetOffset]);
    var isDarkTheme = React.useMemo(function () { return CoreUtils.isDarkTheme(targetRef === null || targetRef === void 0 ? void 0 : targetRef.current); }, [targetRef.current]);
    // Generate key based on offset so a new Popover instance is created
    // when offset changes, to force recomputing position.
    var key = targetOffset.left + "x" + targetOffset.top;
    var renderProps = { isOpen: isOpen, targetOffset: targetOffset };
    return (React.createElement("div", { className: Classes.CONTEXT_MENU2, onContextMenu: handleContextMenu },
        React.createElement(Popover2, __assign({}, restProps, { content: 
            // prevent right-clicking inside our context menu
            React.createElement("div", { onContextMenu: cancelContextMenu }, CoreUtils.isFunction(content) ? content(renderProps) : content), enforceFocus: false, key: key, hasBackdrop: true, isOpen: isOpen, minimal: true, onInteraction: handlePopoverInteraction, popoverClassName: classNames((_b = {}, _b[CoreClasses.DARK] = isDarkTheme, _b)), placement: "right-start", rootBoundary: "viewport", renderTarget: renderTarget, transitionDuration: transitionDuration })),
        CoreUtils.isFunction(children) ? children(renderProps) : children));
};
ContextMenu2.displayName = "Blueprint.ContextMenu2";
//# sourceMappingURL=contextMenu2.js.map